import type { SegmentTrackEvent } from "@beautypie/segment";
import debounce from "lodash.debounce";

import { track } from "tracking/client";
import { getUser } from "lib/user";
import { DataLayerEvent } from "utils/gtm/types";
import { addGTMEvent } from "utils/gtm";
import { Logger } from "lib/logger";
import { CUSTOM_ATTRIBUTE_NAME } from "../../constants";
import { getGTMPurchaseType } from "utils/gtm/utils";

const log = Logger(
  "components/IntersectionDelegate/handlers/productImpression"
);

let impressions: Record<string, HTMLElement["dataset"]> = {};

/**
 * @deprecated This function is kept for backward compatibility.
 * New implementations should use the useProductImpression hook from
 * packages/acquisition/shared/components/ProductCardWrapper/hooks/useProductImpression instead.
 */
export const productImpression = (element: HTMLElement): void => {
  const { sku } = element.dataset;

  if (!sku) return;

  impressions[sku] = element.dataset;
  trackProductListViewed();
};

const storeFromPathname = (pathname: string) => {
  const [first] = pathname.slice(1).split("/");
  return first === "us" ? "us" : "en";
};

const currencyCode = (store: string) => {
  switch (store) {
    case "us": {
      return "USD";
    }
    case "uk":
    default: {
      return "GBP";
    }
  }
};

const inferLocaleFromStore = (store: string) => {
  switch (store) {
    case "us": {
      return "en-US";
    }
    case "uk":
    default: {
      return "en-GB";
    }
  }
};

async function trackGTMImpressions(store: string, skus: string[]) {
  const skuPositions = [
    ...document.querySelectorAll(`[${CUSTOM_ATTRIBUTE_NAME}]`)
  ].map((element) =>
    element.getAttribute(CUSTOM_ATTRIBUTE_NAME)?.split(":").pop()
  );

  const res = await fetch(
    `/api/variants?skus=${skus.join(",")}&locale=${inferLocaleFromStore(
      store
    )}`,
    { next: { revalidate: 60 * 60 } }
  );

  if (res.ok) {
    const { variants } = await res.json();
    const purchaseType = getGTMPurchaseType();

    const data = {
      ecommerce: {
        currencyCode: currencyCode(store),
        impressions: variants.map((variant) => ({
          id: variant?.sku,
          brand: variant?.brandName,
          name: variant?.title,
          variant: variant?.subTitle ?? "",
          price: variant?.pricing.members,
          metric7: variant?.pricing.typical,
          position: skuPositions.indexOf(variant.sku)
        }))
      },
      product_price_type: purchaseType
    };

    addGTMEvent({ name: DataLayerEvent.ProductImpression, data });
  }
}

const trackProductListViewed = debounce(() => {
  const key = `productListViewed${window.location.pathname}`;
  const store = storeFromPathname(window.location.pathname);
  const tracked = JSON.parse(sessionStorage.getItem(key) || "[]");
  const untracked = Object.values(impressions).filter(
    ({ sku }) => !tracked.includes(sku)
  );
  impressions = {};

  if (untracked.length === 0) return;

  try {
    const user = getUser();

    let segments = window.location.pathname.slice(1).split("/");

    if (segments[0] === store) segments = segments.slice(1);

    const categories = segments[0] === "shop" ? segments.slice(1) : [""];

    const payload = {
      event: "Product List Viewed",
      properties: {
        currency: store === "en" ? "GBP" : "USD",
        product_price_type: user.hasMembership
          ? "Member Price"
          : "Typical Price",
        products: untracked.map(({ sku }) => ({
          sku
        })),
        product_category_1: categories[0] || "",
        product_category_2: categories[1],
        product_category_3: categories[2]
      }
    };

    track(payload as SegmentTrackEvent);
  } catch (error) {
    log.error(
      `Failed to send product impressions to Segment`,
      JSON.stringify(error)
    );
  }

  const skus = untracked
    .map(({ sku }) => sku)
    .filter((sku): sku is string => sku !== undefined);

  trackGTMImpressions(store, skus).catch((error) =>
    log.error(
      `Failed to send product impressions to GTM`,
      JSON.stringify(error)
    )
  );

  sessionStorage.setItem(key, JSON.stringify([...tracked, ...skus]));
}, 1000);
